import { TouchpointKind, touchpointKinds } from "@sp-crm/core";

export const cadenceUnitInputs = ["Daily", "Weekly", "Monthly"] as const;

export type CadenceUnitInput = (typeof cadenceUnitInputs)[number];

export interface TouchpointCadence {
    id: string;
    tierListItemId: string;
    kinds: string[];
    intervalUnit: string;
    intervalCount: number;
}

export interface EditableTouchpoint {
    kinds: TouchpointKind[];
    intervalUnit: CadenceUnitInput;
    intervalCount: number;
}

export const emptyEditableTouchpoint = (): EditableTouchpoint => ({
    kinds: [],
    intervalUnit: "Weekly",
    intervalCount: 1,
});

export const joinKindLabelsWithOr = (labels: string[]): string => {
    if (labels.length <= 1) {
        return labels.join("");
    }
    if (labels.length === 2) {
        return `${labels[0]} or ${labels[1]}`;
    }
    return `${labels.slice(0, -1).join(", ")}, or ${labels[labels.length - 1]}`;
};

export const cadenceUnitOptions: { value: CadenceUnitInput; label: string }[] = [
    { value: "Daily", label: "days" },
    { value: "Weekly", label: "weeks" },
    { value: "Monthly", label: "months" },
];

export const asCadenceUnitInput = (value: string): CadenceUnitInput => {
    const match = cadenceUnitInputs.find(unit => unit === value);
    return match ?? "Weekly";
};

const singularUnitLabel: Record<CadenceUnitInput, string> = {
    Daily: "day",
    Weekly: "week",
    Monthly: "month",
};

export const describeInterval = (
    intervalCount: number,
    unit: CadenceUnitInput,
): string => {
    const singular = singularUnitLabel[unit];
    return intervalCount === 1
        ? `every ${singular}`
        : `every ${intervalCount} ${singular}s`;
};

export const summarizeSchedule = (
    kindLabels: string[],
    intervalCount: number,
    unit: CadenceUnitInput,
): string =>
    `${joinKindLabelsWithOr(kindLabels)} ${describeInterval(intervalCount, unit)}`;

export const asTouchpointKinds = (kinds: string[]): TouchpointKind[] =>
    kinds.filter((kind): kind is TouchpointKind =>
        (touchpointKinds as readonly string[]).includes(kind),
    );

export const toggleKind = (
    kinds: TouchpointKind[],
    kind: TouchpointKind,
): TouchpointKind[] =>
    kinds.includes(kind) ? kinds.filter(k => k !== kind) : [...kinds, kind];

export const isAnySelected = (
    kinds: TouchpointKind[],
    availableKinds: TouchpointKind[],
): boolean =>
    availableKinds.length > 0 &&
    kinds.length === availableKinds.length &&
    availableKinds.every(kind => kinds.includes(kind));

export const toggleAny = (
    kinds: TouchpointKind[],
    availableKinds: TouchpointKind[],
): TouchpointKind[] => (isAnySelected(kinds, availableKinds) ? [] : [...availableKinds]);

const tierDefaultTouchpoints: Record<string, EditableTouchpoint[]> = {
    tierA: [
        { kinds: ["visit"], intervalUnit: "Weekly", intervalCount: 1 },
        { kinds: ["email", "call", "text"], intervalUnit: "Weekly", intervalCount: 2 },
    ],
    tierB: [
        { kinds: ["visit"], intervalUnit: "Weekly", intervalCount: 2 },
        { kinds: ["email", "call", "text"], intervalUnit: "Weekly", intervalCount: 4 },
    ],
    tierC: [
        { kinds: ["visit"], intervalUnit: "Weekly", intervalCount: 4 },
        { kinds: ["email", "call", "text"], intervalUnit: "Weekly", intervalCount: 4 },
    ],
    tierD: [
        {
            kinds: ["email", "call", "text", "visit"],
            intervalUnit: "Weekly",
            intervalCount: 4,
        },
    ],
};

//this function is used to handle when the visit option is disabled.  it's more generic than that, in case other options are ever disabled in future as well.
export const suggestedTouchpointsForTier = (
    tierKey: string,
    availableKinds: TouchpointKind[],
): EditableTouchpoint[] =>
    (tierDefaultTouchpoints[tierKey] ?? [])
        .map(touchpoint => ({
            ...touchpoint,
            kinds: touchpoint.kinds.filter(kind => availableKinds.includes(kind)),
        }))
        .filter(touchpoint => touchpoint.kinds.length > 0);

export const parseCadenceCount = (value: string): number | null => {
    const parsed = Number.parseInt(value.trim(), 10);
    if (!Number.isInteger(parsed) || parsed < 1) {
        return null;
    }
    return parsed;
};

export const groupTouchpointsByTier = <T extends TouchpointCadence>(
    touchpoints: T[],
): Map<string, T[]> => {
    const byTier = new Map<string, T[]>();
    for (const touchpoint of touchpoints) {
        const existing = byTier.get(touchpoint.tierListItemId);
        if (existing) {
            existing.push(touchpoint);
        } else {
            byTier.set(touchpoint.tierListItemId, [touchpoint]);
        }
    }
    return byTier;
};
